import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ParentEntityType } from "../../../api/verificationsV2";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { RETRIEVING_DATA_SUBTITLE } from "../../../lib/constants";
import { fetchFormConfigs } from "../../legalTechnicalAnalysis/Logic/formConfigsThunk";
import {
  selectIdentityVerificationsStatus,
  selectProfileVerificationsStatus,
} from "../Logic/verificationsSlice";
import {
  fetchIdentityVerifications,
  fetchProfileVerifications,
} from "../Logic/verificationsThunk";

import IdentityVerificationsTable from "./IdentityVerificationsTable";
import LoanAccountStakeholderNav from "./LoanAccountStakeholderNav";
import ProfileVerificationsTable from "./ProfileVerificationsTable";
import { isCreditAreaOfFraud } from "../../caseinfo/logic/Slice";
import AccountStakeholderNav from "./AccountsStakeholderNav";

const CustomerVerificationsTab: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const isCreditCase = useAppSelector(isCreditAreaOfFraud);

  const [selectedEntityId, setSelectedEntityId] = useState<string>();
  const [selectedStakeholderId, setSelectedStakeholderId] = useState<string>();

  const identityStatus = useAppSelector(selectIdentityVerificationsStatus);
  const profileStatus = useAppSelector(selectProfileVerificationsStatus);

  useEffect(() => {
    if (!caseId) return;

    dispatch(fetchIdentityVerifications(caseId));
    dispatch(fetchProfileVerifications(caseId));
    dispatch(fetchFormConfigs());
  }, [caseId, dispatch]);

  const handleSelectionChange = useCallback(
    (entityId: string, stakeholderId: string) => {
      setSelectedEntityId(entityId);
      setSelectedStakeholderId(stakeholderId);
    },
    [],
  );

  const isLoading = identityStatus === "loading" || profileStatus === "loading";
  const hasSelection = Boolean(selectedEntityId && selectedStakeholderId);
  const parentEntityType = isCreditCase ? ParentEntityType.CREDIT_ACCOUNT : ParentEntityType.ACCOUNT

  return (
    <div className="flex w-full min-w-0 gap-4">
        {isCreditCase ? (
          <LoanAccountStakeholderNav
            selectedLoanReferenceId={selectedEntityId}
            selectedStakeholderId={selectedStakeholderId}
            onSelectionChange={handleSelectionChange}
          />
        ) : (
          <AccountStakeholderNav
            selectedAccountNumber={selectedEntityId}
            selectedStakeholderId={selectedStakeholderId}
            onSelectionChange={handleSelectionChange}
          />
        )
      }

        <div className="flex min-w-0 flex-1 flex-col gap-6">
          {isLoading ? (
            <CustomLoadingSpinner
              title="Loading Verifications..."
              subtitle={RETRIEVING_DATA_SUBTITLE}
              minHeight="min-h-[320px]"
            />
          ) : hasSelection ? (
            <>
              <IdentityVerificationsTable
                parentEntityType={parentEntityType}
                parentEntityId={selectedEntityId as string}
                stakeholderId={selectedStakeholderId as string}
              />

              <ProfileVerificationsTable
                parentEntityType={parentEntityType}
                parentEntityId={selectedEntityId as string}
                stakeholderId={selectedStakeholderId as string}
              />
            </>
          ) : (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-1">
              <p className="text-sm font-semibold text-[#0A0A0A]">
                No Accounts added in the case.
              </p>
              <p className="text-xs font-normal text-[#737373]">
                Add accounts and their respective stakeholders to track their verification details.
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

export default CustomerVerificationsTab;
